import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router';
import { useWorkspace } from '../contexts/workspace-context';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  ChevronRight,
  MoreHorizontal,
  CheckSquare,
  Eye,
  Layers,
  Package,
  Pencil,
  Plus,
  UserPlus,
  Users,
  RotateCcw,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Workspace } from '../types/schema';
import { getLocalStorageCached, setLocalStorageCached } from '../lib/storage-cache';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { WorkspaceSettingsModal } from './workspace-settings-modal';
import { CreateWorkspaceModal } from './create-workspace-modal';
import { InviteMembersModal } from './invite-members-modal';

interface WorkspaceNavItem {
  title: string;
  href: string;
  icon: React.ReactNode;
}

const workspaceNavItems: WorkspaceNavItem[] = [
  { title: 'Tasks', href: '/tasks', icon: <CheckSquare className="h-4 w-4" /> },
  { title: 'Cycles', href: '/cycles', icon: <RotateCcw className="h-4 w-4" /> },
  { title: 'Themes', href: '/themes', icon: <Layers className="h-4 w-4" /> },
  { title: 'Features', href: '/features', icon: <Package className="h-4 w-4" /> },
  { title: 'Views', href: '/views', icon: <Eye className="h-4 w-4" /> },
];

interface WorkspaceItemProps {
  workspace: Workspace;
  isExpanded: boolean;
  isCollapsed: boolean; // sidebar collapsed state
  onToggle: () => void;
  onWorkspaceClick: (workspace: Workspace) => void;
  isDragging?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
}

function WorkspaceItem({
  workspace,
  isExpanded,
  isCollapsed,
  onToggle,
  onWorkspaceClick,
  isDragging = false,
  dragHandleProps,
}: WorkspaceItemProps) {
  const location = useLocation();
  const { currentWorkspace } = useWorkspace();
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const isActive = currentWorkspace?.id === workspace.id;

  const getNavHref = (item: WorkspaceNavItem) => {
    if (item.href === '/themes' || item.href === '/features' || item.href === '/cycles') {
      return `${item.href}/${workspace.id}`;
    }
    return item.href;
  };

  // Only highlight nav items if this is the current workspace AND the path matches
  const isNavActive = (href: string) => {
    if (!isActive) return false; // Only highlight in current workspace
    const fullHref = getNavHref({ href } as WorkspaceNavItem);
    return location.pathname === fullHref || location.pathname.startsWith(fullHref + '/');
  };

  const handleNavClick = () => {
    // When clicking a nav item inside a workspace, switch to that workspace
    onWorkspaceClick(workspace);
  };

  // First letter of workspace name for collapsed state
  const workspaceInitial = workspace.name.charAt(0).toUpperCase();

  if (isCollapsed) {
    // Collapsed sidebar - show only workspace initial with tooltip
    return (
      <>
        <button
          onClick={() => onWorkspaceClick(workspace)}
          className={cn(
            'w-full flex items-center justify-center rounded-component p-2 transition-smooth',
            isActive
              ? 'bg-border text-foreground'
              : 'text-muted-foreground hover:bg-hover hover:text-foreground',
            isDragging && 'opacity-50'
          )}
          title={workspace.name}
        >
          <span className="text-xs font-semibold">{workspaceInitial}</span>
        </button>
        <WorkspaceSettingsModal
          workspace={workspace}
          open={showSettingsModal}
          onOpenChange={setShowSettingsModal}
        />
      </>
    );
  }

  return (
    <>
      <div className={cn('group', isDragging && 'opacity-50 bg-card rounded-component shadow-lg')}>
        {/* Workspace Header */}
        <div
          className={cn(
            'flex items-center gap-0.5 rounded-component transition-smooth',
            isActive ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          {/* Expand/Collapse Button - also acts as drag handle */}
          <button
            {...dragHandleProps}
            onClick={(e) => {
              // Prevent toggle if this was a drag
              if (e.defaultPrevented) return;
              onToggle();
            }}
            className="p-0.5 rounded hover:bg-hover transition-smooth flex-shrink-0 cursor-grab active:cursor-grabbing"
            title="Click to expand, drag to reorder"
          >
            <ChevronRight
              className={cn(
                'h-3.5 w-3.5 transition-transform duration-200',
                isExpanded && 'rotate-90'
              )}
            />
          </button>

          {/* Workspace Name - Clickable to switch workspace */}
          <button
            onClick={() => onWorkspaceClick(workspace)}
            className={cn(
              'flex-1 min-w-0 py-1 px-1 rounded-component text-left transition-smooth',
              'hover:bg-hover hover:text-foreground',
              isActive && 'font-medium'
            )}
          >
            <span className="text-sm truncate block">{workspace.name}</span>
          </button>

          {/* Settings Dropdown - Always visible */}
          <DropdownMenu open={showDropdown} onOpenChange={setShowDropdown}>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  'p-1 rounded hover:bg-hover transition-all flex-shrink-0',
                  'opacity-60 hover:opacity-100',
                  showDropdown && 'opacity-100 bg-hover'
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="right" className="w-48">
              <DropdownMenuItem
                onClick={() => {
                  setShowDropdown(false);
                  setShowSettingsModal(true);
                }}
                className="cursor-pointer"
              >
                <Pencil className="mr-2 h-4 w-4" />
                <span>Rename workspace</span>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer">
                <Link to={`/settings/${workspace.id}`}>
                  <Users className="mr-2 h-4 w-4" />
                  <span>Members & Settings</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setShowDropdown(false);
                  setShowInviteModal(true);
                }}
                className="cursor-pointer"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                <span>Invite members</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Expanded Nav Items */}
        <div
          className={cn(
            'overflow-hidden transition-all duration-200 ease-out',
            isExpanded ? 'max-h-48 opacity-100 mt-0.5' : 'max-h-0 opacity-0'
          )}
        >
          <div className="ml-4 pl-2 border-l border-neutral-200 dark:border-neutral-800 space-y-0.5">
            {workspaceNavItems.map((item) => (
              <Link
                key={item.href}
                to={getNavHref(item)}
                onClick={handleNavClick}
                className={cn(
                  'flex items-center gap-2 rounded-component px-2 py-1 text-sm transition-smooth',
                  isNavActive(item.href)
                    ? 'bg-border text-foreground font-medium'
                    : 'text-muted-foreground hover:bg-hover hover:text-foreground'
                )}
              >
                {item.icon}
                <span>{item.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <WorkspaceSettingsModal
        workspace={workspace}
        open={showSettingsModal}
        onOpenChange={setShowSettingsModal}
      />
      <InviteMembersModal
        workspace={workspace}
        open={showInviteModal}
        onOpenChange={setShowInviteModal}
      />
    </>
  );
}

// Sortable wrapper for WorkspaceItem
interface SortableWorkspaceItemProps {
  workspace: Workspace;
  isExpanded: boolean;
  isCollapsed: boolean;
  onToggle: () => void;
  onWorkspaceClick: (workspace: Workspace) => void;
}

function SortableWorkspaceItem({
  workspace,
  isExpanded,
  isCollapsed,
  onToggle,
  onWorkspaceClick,
}: SortableWorkspaceItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: workspace.id });

  const style = {
    // Only apply Y transform to prevent horizontal distortion
    transform: transform ? `translateY(${transform.y}px)` : undefined,
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <WorkspaceItem
        workspace={workspace}
        isExpanded={isExpanded}
        isCollapsed={isCollapsed}
        onToggle={onToggle}
        onWorkspaceClick={onWorkspaceClick}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners } as React.HTMLAttributes<HTMLButtonElement>}
      />
    </div>
  );
}

interface WorkspaceSwitcherProps {
  isCollapsed?: boolean;
}

export function WorkspaceSwitcher({ isCollapsed = false }: WorkspaceSwitcherProps) {
  const { workspaces, currentWorkspace, setCurrentWorkspace, loading } = useWorkspace();
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<Set<number>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [orderedWorkspaceIds, setOrderedWorkspaceIds] = useState<number[]>([]);

  // Configure dnd sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Start drag after 8px movement
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize ordered workspace IDs when workspaces load
  useEffect(() => {
    if (workspaces.length > 0) {
      // Try to restore saved order from localStorage (using cache)
      const savedOrder = getLocalStorageCached('workspace_order');
      if (savedOrder) {
        try {
          const parsed = JSON.parse(savedOrder) as number[];
          // Filter to only include workspaces that still exist
          const validIds = parsed.filter(id => workspaces.some(w => w.id === id));
          // Add any new workspaces that aren't in the saved order
          const newIds = workspaces.filter(w => !validIds.includes(w.id)).map(w => w.id);
          setOrderedWorkspaceIds([...validIds, ...newIds]);
        } catch {
          setOrderedWorkspaceIds(workspaces.map(w => w.id));
        }
      } else {
        setOrderedWorkspaceIds(workspaces.map(w => w.id));
      }
    }
  }, [workspaces]);

  // Get ordered workspaces
  const orderedWorkspaces = orderedWorkspaceIds
    .map(id => workspaces.find(w => w.id === id))
    .filter((w): w is Workspace => w !== undefined);

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setOrderedWorkspaceIds((items) => {
        const oldIndex = items.indexOf(active.id as number);
        const newIndex = items.indexOf(over.id as number);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        // Save to localStorage (using cache)
        setLocalStorageCached('workspace_order', JSON.stringify(newOrder));
        return newOrder;
      });
    }
  };

  // Auto-expand current workspace on initial load
  useEffect(() => {
    if (currentWorkspace && !expandedWorkspaces.has(currentWorkspace.id)) {
      setExpandedWorkspaces((prev) => new Set(Array.from(prev).concat([currentWorkspace.id])));
    }
  }, [currentWorkspace?.id]);

  // Functional setState (Vercel Best Practice: rerender-functional-setstate)
  const toggleWorkspace = useCallback((workspaceId: number) => {
    setExpandedWorkspaces((prev) => {
      const next = new Set(prev);
      if (next.has(workspaceId)) {
        next.delete(workspaceId);
      } else {
        next.add(workspaceId);
      }
      return next;
    });
  }, []);

  // Stable callback with useCallback (Vercel Best Practice)
  const handleWorkspaceClick = useCallback((workspace: Workspace) => {
    setCurrentWorkspace(workspace);
    // Also expand the workspace when clicked
    setExpandedWorkspaces((prev) => {
      if (!prev.has(workspace.id)) {
        return new Set(Array.from(prev).concat([workspace.id]));
      }
      return prev;
    });
  }, [setCurrentWorkspace]);

  if (loading) {
    return (
      <div className="space-y-2 px-2">
        {!isCollapsed && (
          <div className="px-3 py-1 text-small text-tertiary font-medium">
            Workspaces
          </div>
        )}
        <div className="space-y-1">
          {[1, 2].map((i) => (
            <div
              key={i}
              className={cn(
                'rounded-component bg-muted animate-pulse',
                isCollapsed ? 'h-10 w-10 mx-auto' : 'h-8 mx-2'
              )}
            />
          ))}
        </div>
      </div>
    );
  }

  if (workspaces.length === 0) {
    return (
      <>
        <div className="px-2">
          {!isCollapsed && (
            <div className="flex items-center justify-between px-3 py-1">
              <span className="text-small text-tertiary font-medium">Workspaces</span>
              <button
                onClick={() => setShowCreateModal(true)}
                className="p-0.5 rounded hover:bg-hover transition-smooth text-tertiary hover:text-foreground group relative"
                title="Create workspace"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {isCollapsed && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-full flex items-center justify-center p-2 rounded-component text-muted-foreground hover:bg-hover hover:text-foreground transition-smooth"
              title="Create workspace"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}
          {!isCollapsed && (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              No workspaces yet
            </p>
          )}
        </div>

        <CreateWorkspaceModal
          open={showCreateModal}
          onOpenChange={setShowCreateModal}
        />
      </>
    );
  }

  return (
    <>
      <div className="space-y-1">
        {!isCollapsed && (
          <div className="flex items-center justify-between px-3 py-1">
            <span className="text-small text-tertiary font-medium">Workspaces</span>
            <button
              onClick={() => setShowCreateModal(true)}
              className="p-0.5 rounded hover:bg-hover transition-smooth text-tertiary hover:text-foreground"
              title="Create workspace"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedWorkspaceIds}
            strategy={verticalListSortingStrategy}
          >
            <div className={cn('space-y-0.5', isCollapsed ? 'px-2' : 'px-2')}>
              {orderedWorkspaces.map((workspace) => (
                <SortableWorkspaceItem
                  key={workspace.id}
                  workspace={workspace}
                  isExpanded={expandedWorkspaces.has(workspace.id)}
                  isCollapsed={isCollapsed}
                  onToggle={() => toggleWorkspace(workspace.id)}
                  onWorkspaceClick={handleWorkspaceClick}
                />
              ))}

              {/* Create button for collapsed state */}
              {isCollapsed && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="w-full flex items-center justify-center p-2 rounded-component text-muted-foreground hover:bg-hover hover:text-foreground transition-smooth"
                  title="Create workspace"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <CreateWorkspaceModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
      />

    </>
  );
}
